import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Client } from '../../types';
import { formatArgentinaDate } from '../../utils/dateUtils';

interface DeliveryNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (deliveryNote: any) => void;
  clients: Client[];
}

interface DeliveryNoteItem {
  id: string;
  description: string;
  quantity: number;
}

const DeliveryNoteModal: React.FC<DeliveryNoteModalProps> = ({
  isOpen,
  onClose,
  onSave,
  clients,
}) => {
  const [formData, setFormData] = useState({
    clientId: '',
    date: new Date().toISOString().split('T')[0],
    carrierName: '',
    deliveredBy: '',
    items: [] as DeliveryNoteItem[],
  });
  
  const [error, setError] = useState<string | null>(null);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        clientId: '',
        date: new Date().toISOString().split('T')[0],
        carrierName: '',
        deliveredBy: '',
        items: [],
      });
      setError(null);
    }
  }, [isOpen]);
  
  // Add a new item to the delivery note
  const addItem = () => {
    const newItem: DeliveryNoteItem = {
      id: uuidv4(),
      description: '',
      quantity: 1,
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem],
    });
  };
  
  // Update an item in the delivery note
  const updateItem = (id: string, field: keyof DeliveryNoteItem, value: any) => {
    setFormData({
      ...formData,
      items: formData.items.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      ),
    });
  };
  
  // Remove an item from the delivery note
  const removeItem = (id: string) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.id !== id),
    });
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.clientId) {
      setError('Debe seleccionar un cliente');
      return;
    }
    
    if (formData.items.length === 0) {
      setError('Debe agregar al menos un ítem al remito');
      return;
    }
    
    for (const item of formData.items) {
      if (!item.description) {
        setError('Todos los ítems deben tener una descripción');
        return;
      }
      if (item.quantity <= 0) {
        setError('Todos los ítems deben tener una cantidad mayor a 0');
        return;
      }
    }
    
    // Get client details
    const client = clients.find(c => c.id === formData.clientId);
    if (!client) {
      setError('Cliente no encontrado');
      return;
    }
    
    // Generate delivery note number
    const number = `R-${Date.now().toString().substring(6)}`;
    
    // Create delivery note object
    const deliveryNote = {
      id: uuidv4(),
      number,
      date: formData.date,
      clientId: formData.clientId,
      clientName: client.name,
      clientAddress: client.address || '',
      clientCity: client.city || '',
      clientProvince: client.province || '',
      clientCuit: client.cuit || '',
      carrierName: formData.carrierName,
      deliveredBy: formData.deliveredBy,
      items: formData.items,
      createdAt: new Date().toISOString(),
    };
    
    // Save delivery note
    onSave(deliveryNote);
  };
  
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            Nuevo Remito
          </Dialog.Title>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Select
                label="Cliente"
                value={formData.clientId}
                onChange={(value) => setFormData({ ...formData, clientId: value })}
                options={[
                  { value: '', label: 'Seleccionar cliente...' },
                  ...clients.map(client => ({
                    value: client.id,
                    label: client.name,
                  }))
                ]}
                required
              />
              
              <Input
                type="date"
                label="Fecha"
                id="date"
                name="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
              
              <Input
                label="Transportista"
                id="carrierName"
                name="carrierName"
                value={formData.carrierName}
                onChange={(e) => setFormData({ ...formData, carrierName: e.target.value })}
                placeholder="Nombre del transportista"
              />
              
              <Input
                label="Entregado por"
                id="deliveredBy"
                name="deliveredBy"
                value={formData.deliveredBy}
                onChange={(e) => setFormData({ ...formData, deliveredBy: e.target.value })}
                placeholder="Nombre de quien entrega"
              />
            </div>
            
            {/* Items */}
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Items</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={addItem}
                >
                  Agregar Item
                </Button>
              </div>
              
              {formData.items.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-8">
                    <Input
                      label="Descripción"
                      id={`desc-${item.id}`}
                      name={`desc-${item.id}`}
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      label="Cantidad"
                      type="number"
                      id={`qty-${item.id}`}
                      name={`qty-${item.id}`}
                      value={item.quantity.toString()}
                      onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value))}
                      min="1"
                      step="1"
                      required
                    />
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="mt-7 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
              
              {formData.items.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                  No hay items agregados. Haga clic en "Agregar Item" para comenzar.
                </div>
              )}
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-sm">
                {error}
              </div>
            )}
            
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
              >
                Guardar Remito
              </Button>
            </div>
          </form>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DeliveryNoteModal;